import axios, { AxiosError } from 'axios';
import { API_GATEWAY_BASE_URL } from '@/core/global-data/constants.data';
import { clearAuthTokens } from '@/features/auth/guard/utils';
import router from '@/core/global-router';
import { changeLanguage } from 'i18next';
import { toast } from 'sonner';

// ------------- AXIOS DEFAULTS SETTINGS -------------
// ---------------------------------------------------
const defaultURL = API_GATEWAY_BASE_URL;

axios.defaults.baseURL = defaultURL;

// ------------- GLOBAL ERROR HANDLER -------------

const handleUnauthorized = () => {
    // 1. Clear authentication tokens
    clearAuthTokens();

    // 2. Redirect to login page
    router.navigate('/login');

    // 3. Optional: Refresh the page to clear application state
    window.location.reload();
};

// ------------- CREATE AXIOS INTERCEPTORS -------------
// ----------------------------------------------------

axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const status = error.response?.status;
        const isUnauthorized = status === 401;

        if (isUnauthorized && !error.config?.url?.includes('/login')) {
            handleUnauthorized();
        }

        if (status === 403 && error.response?.data?.message) {
            toast.error(error.response?.data?.message);
        }

        return Promise.reject(error);
    },
);

// -------------------------------------------------------

export const endpoints = {
    auth: {
        login: '/login',
        profile: '/user',
        me: '/me',
        changePassword: '/change-password',
        forgotPassword: '/forgot-password',
        resetPassword: '/reset-password',
    },
    dashboard: {
        analytics: '/dashboard',
    },
    companies_and_suppliers: {
        dashboard: '/sellers-analytics',
        list: '/sellers-paginated',
        create: '/sellers-store',
        update: '/sellers-update/',
        delete: '/sellers-delete/',
        show: '/sellers-show/',
        select: '/sellers-simple',
    },

    customer: {
        create: '/customers-store',
        update: '/customers-update/',
        delete: '/customers-delete/',
        list: '/customers-paginated',
        select: '/customers-simple',
        show: '/customers-show/',
        block: '/customers-add-to-black-list/',
        logs: '/logs/user',
        logscleanup: '/logs/cleanup/',

        dashboard: '/customers-analytics',
        dashboardType: '/customers-analytics-type',
        images: '/customer-update-personal-images/',
        license: '/customer-update-license-images/',
        dashboardNewCustomers: '/customer-new-dashboard',
        typeTrack: '/customers-type-tracks/',
        typeTracks: '/customers-type-tracks',
    },

    complaint: {
        list: '/customer-complaints-paginated',
        show: '/customer-complaints-show/',
        update: '/customer-complaints-resolve/',
        // update: '/customers-complaints/',
        create: '/customer-complaints-store',
        delete: '/customers-delete/',
        resolve: '/customer-complaints-resolve/',
        dashboard: '/customer-complaints-statistics',
        open: '/customer-complaints-open',
    },

    cutomerContact: {
        list: '/customer-emails-paginated',
        create: '/customer-emails-store',
        dashboard: '/customer-emails-statistics',
        show: '/customer-emails-show/', //customer-emails-show
        update: '/customer-emails-update',
        email: '/customers-emails/',
    },

    services: {
        create: '/services-store',
        update: '/services-update',
        delete: '/services-delete/',
        list: '/services-paginated',
        show: '/services-show',
        dashboard: '/services-analytics',
        select: '/services-simple',
    },

    products: {
        list: '/products-paginated',
        create: '/products-store',
        update: '/products-update',
        delete: '/products-delete/',
        show: '/products-show',
        dashboard: '/products-analytics',
        select: '/products-simple',
    },
    finance: {
        payments: '/orders-payment-analytics',
    },
    notification: {
        list: '/notification-list',
        count: '/notification-count',
        delete: '/notification-delete/',
        read: '/notification-mark-read/',
        allRead: '/notification-mark-all-read',
        mail: '/notification-mail',
        generate: '/notification-generate',
        sendEmail: '/notification-send-emails',
    },

    orderproducts: {
        list: '/product-orders-paginated',
        create: '/product-orders-store',
        update: '/product-orders-update',
        delete: '/product-orders-delete/',
        show: '/product-orders-show',
        dashboard: '/product-orders-analytics',
        post: '/website-product-service',
        get: '/website-products',
        // select: '/products-simple',
    },
    branch: {
        create: '/branches-store',
        list: '/branches-paginated',
        show: '/branches-show/',
        update: '/branches-update/',
        delete: '/branches-delete/',
        dashboard: '/branches-analytics',
        select: '/branches-simple',
    },
    salesPoints: {
        create: '/sales-points-store',
        list: '/sales-points-paginated',
    },
    employee: {
        list: '/users-paginated',
        show: '/users-show/',
        create: '/users-store',
        update: '/users-update/',
        delete: '/users-delete/',
        dashboard: '/users-analytics',
        select: '/users-simple',
        attendance: '/users-attendance',
        salary: '/users-salary/',
        salaryList: '/users-salary-list',
        logs: '/user-logs/user/',
        usersOrdersProducts: '/users-orders-and-product-orders/',
    },
    roles: {
        list: '/role-list',
        create: '/role-create',
        update: '/role-update/',
        delete: '/role-delete/',
        show: '/role-permissions/',
    },
    permissions: {
        list: '/permission-list',
        create: '/permission-create',
        update: '/permission-update/',
        delete: '/permission-delete/',
    },
    userLimits: {
        list: '/user-limits',
    },
    orders: {
        create: '/orders-store',
        update: '/orders-update',
        delete: '/orders-delete/',
        list: '/orders-paginated',
        show: '/orders-show',
        dashboard: '/orders-analytics',
        status: '/orders-update-status',
    },
    subscriptions: {
        list: '/subscriptions-paginated',
        create: '/subscriptions-store',
        dashboard: '/subscriptions-analytics',
        update: '/subscriptions-update/',
        delete: '/subscriptions-delete/',
        show: '/subscriptions-show/',
        subscriptions: '/customer-subscriptions',
        planList: '/subscription-plan-list',
        planCreate: '/subscription-plan-create',
        planUpdate: '/subscription-plan-update/',
        planDelete: '/subscription-plan-delete/',
        tenantList: '/tenant-subscription-list',
        tenantUpdate: '/tenant-subscription-update/',
        upgradeList: '/subscription-upgrade-request-list',
        upgradeUpdate: '/subscription-upgrade-request-update/',
        upgradeRequest: '/subscription-upgrade-request',
        mySubscription: '/my-subscription',
        plansAvailable: '/subscription-plans-available',
        platformSettings: '/platform-settings',
        tenantCreate: '/tenant-create',
        tenantUserUpdate: '/tenant-user-update/',
        tenantDelete: '/tenant-delete/',
        tenantSubscriptionEnd: '/tenant-subscription-end/',
        platformUserList: '/platform-user-list',
        tenantUsers: '/tenant-users/',
        loginAs: '/tenant-login-as/',
    },

    loan: {
        list: '/loan-request-list-paginate',
        show: '/loan-request-show/',
        update: '/loan-request-update/',
        create: '/loan-request-create',
        delete: '/loan-request-delete/',
    },
    letterRequests: {
        paginate: '/letter-request-list-paginate',
        create: '/letter-request-create',
        update: '/letter-request-update/',
        delete: '/letter-request-delete/',
    },
    extraServices: {
        list: '/service-request-list-paginate',
        show: '/service-request-show/',
        create: '/service-request-create',
        update: '/service-request-update/',
        delete: '/service-request-delete/',
    },
    employeeComplaint: {
        list: '/employee-complaint-list-paginate',
        show: '/employee-complaint-show/',
        update: '/employee-complaint-update/',
        create: '/employee-complaint-create',
        delete: '/employee-complaint-delete/',
    },

    invoices: {
        list: '/invoices-paginated',
        create: '/invoices-store',
        show: '/invoices-show/',
        delete: '/invoices-delete/',
        dashboard: '/invoices-analytics',
    },
    sales: {
        list: '/sales-paginated',
        create: '/sales-store',
        show: '/sales-show/',
        delete: '/sales-delete/',
        dashboard: '/sales-analytics',
    },
    proformaInvoices: {
        list: '/proforma-invoices-paginated',
        create: '/proforma-invoices-store',
        show: '/proforma-invoices-show/',
        delete: '/proforma-invoices-delete/',
    },
    sellers: {
        list: '/sellers-paginated',
        create: '/sellers-store',
        select: '/sellers-simple',
        show: '/sellers-show/',
        update: '/sellers-update/',
        delete: '/sellers-delete/',
        dashboard: '/sellers-analytics',
    },
    expenses: {
        list: '/expenses-paginated',
        create: '/expenses-store',
        show: '/expenses-show/',
        delete: '/expenses-delete/',
        dashboard: '/expenses-analytics',
    },
    accountsGuide: {
        list: '/accounts-guide-list',
        create: '/accounts-guide-store',
        reorder: '/accounts-guide-reorder',
        select: '/accounts-guide-simple',
    },
    costCenters: {
        list: '/cost-centers-list',
        create: '/cost-centers-store',
        reorder: '/cost-centers-reorder',
        select: '/cost-centers-simple',
        report: '/cost-centers-report',
        assignInvoice: '/cost-centers-assign-invoice',
    },
    assets: {
        list: '/assets-paginated',
        create: '/assets-store',
        show: '/assets-show/',
        update: '/assets-update/',
        delete: '/assets-delete/',
        select: '/assets-simple',
    },
    warehouses: {
        list: '/warehouses-paginated',
        create: '/warehouses-store',
        show: '/warehouses-show/',
        update: '/warehouses-update/',
        delete: '/warehouses-delete/',
        dashboard: '/warehouses-analytics',
        select: '/warehouses-simple',
        movements: '/warehouse-movements-paginated',
        transfers: '/product-transfers-paginated',
        transfer: '/warehouse-transfer-store',
    },
    warehouseInventory: {
        list: '/warehouse-inventory-paginated',
        create: '/warehouse-inventory-store',
        show: '/warehouse-inventory-show/',
        update: '/warehouse-inventory-update/',
        delete: '/warehouse-inventory-delete/',
    },
    generalAccountsSettings: {
        list: '/general-accounts-settings-paginated',
        create: '/general-accounts-settings-store',
        show: '/general-accounts-settings-show/',
        update: '/general-accounts-settings-update/',
        delete: '/general-accounts-settings-delete/',
        journalEntry: '/general-accounts-settings-journal/',
        closePeriod: '/general-accounts-settings-close/',
    },
    closedPeriods: {
        list: '/closed-periods-paginated',
        create: '/closed-periods-store',
        show: '/closed-periods-show/',
        update: '/closed-periods-update/',
        delete: '/closed-periods-delete/',
    },
    periodBudget: {
        report: '/period-budget-report',
        saveDraft: '/period-budget-draft',
    },
    journalEntries: {
        list: '/journal-entries-paginated',
        create: '/journal-entries-store',
        show: '/journal-entries-show/',
        update: '/journal-entries-update/',
        delete: '/journal-entries-delete/',
        repeat: '/journal-entries-repeat',
    },
    purchaseOrders: {
        list: '/purchase-orders-paginated',
        create: '/purchase-orders-store',
        show: '/purchase-orders-show/',
        update: '/purchase-orders-update/',
        delete: '/purchase-orders-delete/',
        stages: '/purchase-orders-stages',
        dashboard: '/purchase-orders-analytics',
        reports: '/purchase-orders-reports',
    },
    pos: {
        products: '/pos-products',
        transactions: '/pos-transactions',
    },
    reports: {
        analytics: '/reports-analytics',
        salesChart: '/reports-sales-chart',
        topSellers: '/reports-top-sellers',
        invoices: '/reports-invoices',
        salesByProducts: '/reports-sales-by-products',
        inventory: '/reports-inventory',
    },
    profitReport: {
        analytics: '/profit-analytics',
        revenueExpensesChart: '/profit-revenue-expenses-chart',
    },
};
